import * as d3 from "d3"

import { createSvgDefs } from "./nexusCreateSvgDefs"
import { hideTimelineTooltip } from "./timelineTooltip"
import { findDateX, sortByName, sortByStart, createBar, updateBars, createRowLine, updateRowLine, createName, updateName, createVLine, createVLegend, updateVLine, moveCursor, hideCursor, updateVLegend, createHandler, updateHandlers, createLabel, updateLabel } from "./timelineFunctions"
import { layout, timing } from "./nexusConfig"
import { CATEGORY, COLOR, JARGON, TIMELINE, TENSE, TENSE_ENUM } from "../../../modules/constants"

// import { wrap } from "./wrap"
import { isMobile, getFrame, getNameProp /* getStatusLevel, getFullStatus */ } from "../../../modules/utilities"

// file global variables
const mobile = isMobile()

let svg = {
  element: {},
}

let nexus = {
  checkboxes: {},
  group: {},
  background: {},
  title: {},
  links: {},
}

// let browserIsEdge = false
// let showingPrompt = false

export const createTimelineD3 = () => {
  // console.log("createTimelineD3")

  initTimelineSvg()
}

export const updateTimelineD3 = props => {
  // console.log("updateTimelineD3 props:", props)

  // only run the update code if nexus has previously been initialised
  if (!nexus.initialised) return

  const { data, handleOnClick, filteredItems } = props
  // console.log('filteredItems:', filteredItems)

  const hubNode = data.nodes[0]

  nexus.showPosition = hubNode.entity.showPosition
  nexus.category = hubNode.entity.category
  nexus.hubId = hubNode.id
  nexus.name = getNameProp.toPresent(hubNode.entity.name)

  nexus.lseCompany = data.links && data.links.length >= 1 && hubNode.entity.companyProps?.lse
  nexus.secondLevel = data.enableSecondLevel

  if (nexus.category === CATEGORY.COMPANY && hubNode.entity.companyProps?.companyProfile) {

    const companyProfile = hubNode.entity.companyProps?.companyProfile

    nexus.foundedDate = companyProfile.dateOfCreation
      ? new Date(companyProfile.dateOfCreation)
      : null

    nexus.ceasedDate = companyProfile.dateOfCessation
      ? new Date(companyProfile.dateOfCessation)
      : null
  } else { // clear founded and ceased dates
    nexus.foundedDate = null
    nexus.ceasedDate = null
  }

  hideTimelineTooltip()

  nexus.checkboxes = props.checkboxes

  // sort links
  filteredItems.sort(sortByName)

  if (!nexus.secondLevel) filteredItems.sort(sortByStart)
  // else nexus.filteredItems.sort(sortByDuration) // showing second level

  nexus.links = nexus.links.data(filteredItems, d => d.id) // rejoin data
  nexus.numLinks = nexus.links._enter[0].length

  // reset the svg
  resetTimelineSvg()
  // if (!showingPrompt) createPrompt() else removePrompt()

  // apply the general update pattern to the grid
  enterGrid()
  updateGrid()

  // apply the general update pattern to the links
  nexus.links.exit().select(".handler").attr("pointer-events", "none") // disable click for links that are going
  nexus.links.exit().transition().duration(timing.slow).style("opacity", 1e-6).remove() // exit links
  enterLinks(handleOnClick)
  updateLinks()
}

export const resizeTimelineD3 = () => {
  // console.log("resizeTimelineD3")

  resetTimelineSvg()
  updateGrid()
  updateLinks()

  // resizeConnectionInfo(nexus)
  // repositionPrompt()
}

//#region links
const enterLinks = handleOnClick => {
  // console.log("nexus:", nexus)

  let newLinks = nexus.links.enter() // find the new links that need to be added
    .append("g")
    .attr("class", "link")
    .attr("transform", "translate(" + (10) + "," + (10) + ")")

  createBar({ parent: newLinks, type: "link-bar", height: TIMELINE.ROW_HEIGHT - TIMELINE.VERTICAL_GAP })

  createBar({ parent: newLinks, type: "future-bar", height: TIMELINE.ROW_HEIGHT - TIMELINE.VERTICAL_GAP })
  createName({ parent: newLinks, x: 10, y: TIMELINE.ROW_HEIGHT / 2 })
  createHandler({ parent: newLinks, handleOnClick, mobile, layout, nexus })
  createRowLine({ parent: newLinks, type: "sex", nexus }) // male or female bar
  createRowLine({ parent: newLinks, type: "ned9", nexus }) // 9 year tenure line for NEDs
  if (!nexus.portrait) createLabel({ parent: newLinks, y: TIMELINE.ROW_HEIGHT / 2, nexus })

  nexus.links = nexus.links.merge(newLinks)
}

const updateLinks = () => {
  // console.log("updateLinks")

  const todayX = findDateX({ date: new Date(), plotWidth: nexus.width, foundedX: null })

  nexus.links // move the link groups
    .transition()
    .duration(timing.slow)
    .attr("transform", (d, i) => "translate(" + (0) + "," + ((i + 1) * TIMELINE.ROW_HEIGHT) + ")")

  updateBars({ type: "link-bar", nexus, todayX })
  updateBars({ type: "future-bar", nexus, todayX })
  updateName(nexus)
  updateHandlers(nexus)
  updateRowLine({ nexus, type: "sex" })
  updateRowLine({ nexus, type: "ned9" })
  updateLabel(nexus)
}
//#endregion links

//#region grid
const enterGrid = () => {
  // console.log("enterGrid")

  if (nexus.grid) { // if grid already exists, delete it and all D3 elements
    nexus.grid.selectAll("line").remove()
    nexus.grid.selectAll("text").remove()
    delete nexus.grid
  }
  // console.log("nexus.grid:", nexus.grid)

  // create grid group
  nexus.grid = nexus.group
    .append("g")
    .attr("class", "grid")
  // .attr("transform", "translate(" + (10) + "," + (10) + ")")

  // create year legends and lines
  nexus.grid.legend = []
  nexus.grid.line = []
  // const numGrids = (TIMELINE.END_YEAR - TIMELINE.START_YEAR) / TIMELINE.GRID_SPACE
  const numYears = TIMELINE.END_YEAR - TIMELINE.START_YEAR

  for (let gridNum = 0; gridNum < numYears; ++gridNum) {
    const year = TIMELINE.START_YEAR + gridNum
    const date = new Date(`01/01/${year}`)
    const x = findDateX({ date: date, plotWidth: nexus.width, foundedX: null }) // gridNum * nexus.width / numGrids

    if (gridNum % TIMELINE.MAJOR_GRID_LINE_SPACING === 0) { // major grid line with legend
      nexus.grid.line[gridNum] = createVLine({
        parent: nexus.grid, color: "black", width: 1.5, x, y1: 0, y2: nexus.height
      })
      nexus.grid.legend[gridNum] = createVLegend({
        parent: nexus.grid, text: year, color: "black", x, y: TIMELINE.ROW_HEIGHT * 0.45,
        align: "start",
      })
    } else { // minor grid line
      nexus.grid.line[gridNum] = createVLine({
        parent: nexus.grid, color: "black", width: 0.5, x, y1: 0, y2: nexus.height
      })
    }
  }

  // create "today" legend and line
  const todayX = findDateX({ date: new Date(), plotWidth: nexus.width, foundedX: null })
  nexus.grid.today = {}

  nexus.grid.today.line = createVLine({
    parent: nexus.grid, color: d3.rgb(COLOR.BRAND.STD), width: 3,
    x: todayX, y1: TIMELINE.ROW_HEIGHT * 0.6, y2: nexus.height
  })

  nexus.grid.today.legend = createVLegend({
    parent: nexus.grid, text: "now", color: d3.rgb(COLOR.BRAND.STD),
    x: todayX, y: TIMELINE.ROW_HEIGHT * 0.85,
    align: "start",
  })

  // founded legend and line
  if (nexus.category === CATEGORY.COMPANY) {
    const foundedX = findDateX({ date: new Date(nexus.foundedDate), plotWidth: nexus.width, foundedX: null })
    // console.log("foundedX: ", foundedX)
    nexus.grid.founded = {}
    nexus.grid.founded.line = createVLine({
      parent: nexus.grid, color: TENSE[TENSE_ENUM.CURRENT].COLOR, width: 3,
      x: foundedX, y1: TIMELINE.ROW_HEIGHT * 0.6, y2: nexus.height
    })
    nexus.grid.founded.legend = createVLegend({
      parent: nexus.grid, text: "founded", color: TENSE[TENSE_ENUM.CURRENT].COLOR,
      x: foundedX, y: TIMELINE.ROW_HEIGHT * 0.85,
      align: "end",
    })

    if (nexus.ceasedDate) {
      const ceasedX = findDateX({ date: new Date(nexus.ceasedDate), plotWidth: nexus.width, foundedX: null })
      nexus.grid.ceased = {}
      nexus.grid.ceased.line = createVLine({
        parent: nexus.grid, color: TENSE[TENSE_ENUM.PAST].COLOR, width: 3,
        x: ceasedX, y1: TIMELINE.ROW_HEIGHT * 0.6, y2: nexus.height
      })
      nexus.grid.ceased.legend = createVLegend({
        parent: nexus.grid, text: JARGON.COMPANY_TIMELINE.CEASED.toLowerCase(), color: TENSE[TENSE_ENUM.PAST].COLOR,
        x: ceasedX, y: TIMELINE.ROW_HEIGHT * 0.85, align: "end",
      })
    }
  }

  // create cursor line
  if (!mobile) {
    nexus.grid.cursor = {}
    nexus.grid.cursor.line = createVLine({
      parent: nexus.grid, color: COLOR.BRAND.STD, width: 1,
      x: 10, y1: 0, y2: nexus.height
    })
    nexus.grid.cursor.legend = createVLegend({
      parent: nexus.grid, text: "date", color: COLOR.BRAND.STD,
      x: 500, y: TIMELINE.ROW_HEIGHT * 0.85, align: "start",
    })
  }
}

const updateGrid = () => {
  // console.log("updateGrid")

  // const numGrids = (TIMELINE.END_YEAR - TIMELINE.START_YEAR) / TIMELINE.GRID_SPACE
  const numYears = TIMELINE.END_YEAR - TIMELINE.START_YEAR

  for (let gridNum = 0; gridNum < numYears; ++gridNum) {
    const year = TIMELINE.START_YEAR + gridNum
    const date = new Date(`01/01/${year}`)
    const x = findDateX({ date: date, plotWidth: nexus.width, foundedX: null }) // gridNum * nexus.width / numGrids

    // update year legend and line
    if (gridNum % TIMELINE.MAJOR_GRID_LINE_SPACING === 0) { // major grid line with legend
      updateVLine({ line: nexus.grid.line[gridNum], x, y2: nexus.height, opacity: 0.2 })
      updateVLegend({ legend: nexus.grid.legend[gridNum], x: x, opacity: 1, align: "start", })
    } else { // minor grid line
      updateVLine({ line: nexus.grid.line[gridNum], x, y2: nexus.height, opacity: 0.2 })
    }
  }

  // update today legend and line
  const todayX = findDateX({ date: new Date(), plotWidth: nexus.width, foundedX: null })
  updateVLine({ line: nexus.grid.today.line, x: todayX, y2: nexus.height, opacity: 0.75 })
  updateVLegend({ legend: nexus.grid.today.legend, x: todayX, opacity: 1, align: "start", })

  // founded legend and line
  if (nexus.category === CATEGORY.COMPANY) {
    const foundedX = findDateX({ date: new Date(nexus.foundedDate), plotWidth: nexus.width, foundedX: null })
    updateVLine({ line: nexus.grid.founded.line, x: foundedX, y2: nexus.height, opacity: 0.5 })
    updateVLegend({ legend: nexus.grid.founded.legend, x: foundedX, opacity: 1, align: "end", })

    if (nexus.ceasedDate) {
      const ceasedX = findDateX({ date: new Date(nexus.ceasedDate), plotWidth: nexus.width, foundedX: null })
      updateVLine({ line: nexus.grid.ceased.line, x: ceasedX, y2: nexus.height, opacity: 0.5 })

      updateVLegend({ legend: nexus.grid.ceased.legend, x: ceasedX, opacity: 1, align: "end", })
    }
  }
}
//#endregion grid

//#region svg
const initTimelineSvg = () => {
  svg.element = d3.select(".timeline-div")
    .append("svg")
    .attr("id", "svg")
    .attr("class", "svg")

  const svgDoc = document.getElementById("svg")
  nexus.svgX = svgDoc.getBoundingClientRect().x
  nexus.svgY = svgDoc.getBoundingClientRect().y
  // console.log("svgX = " + svgX )

  createSvgDefs(svg.element)

  // initialise groups
  nexus.group = svg.element.append("g")
    .attr("id", "nexus")
    .attr("class", "nexus")

  nexus.background = nexus.group
    .append("rect")
    .attr("class", "nexusBackground")
    .on('mousemove', event => !mobile && moveCursor({ event, nexus }))
    .on('mouseout', () => !mobile && hideCursor(nexus))

  nexus.links = nexus.group.append("g").attr("class", "link").selectAll("g")
  nexus.initialised = true
}

const resetTimelineSvg = () => {

  const frame = getFrame()

  nexus.width = frame.width
  nexus.frameHeight = frame.height
  nexus.height = Math.max((frame.height - 10), (nexus.numLinks + 1) * TIMELINE.ROW_HEIGHT)
  nexus.portrait = frame.portrait

  svg.element.attr("width", nexus.width).attr("height", nexus.height)

  nexus.background
    .attr("width", nexus.width)
    .attr("height", nexus.height)
}

//#endregion svg

